import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchContent, type ContentMap } from '@/api/client';
import { aboutFeatures, aboutStats } from '@/mocks/about';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function AboutSection() {
  const { t, i18n } = useTranslation('home');
  const [content, setContent] = useState<ContentMap>();

  useEffect(() => {
    let cancelled = false;
    const lang = i18n.language === 'es' ? 'es' : 'en';
    fetchContent(lang).then((data) => {
      if (cancelled) return;
      setContent(data || {});
    });
    return () => { cancelled = true; };
  }, [i18n.language]);

  const getContent = (key: string, fallbackKey: string) => {
    const val = content?.[key]?.trim();
    if (val) return val;
    const fb = t(fallbackKey, fallbackKey);
    if (fb !== fallbackKey) return fb;
    // Fallbacks seguros para evitar keys técnicas visibles
    if (key === 'about_title') {
      return i18n.language === 'es'
        ? 'Sobre Nosotros'
        : 'About Us';
    }
    if (key === 'about_description') {
      return i18n.language === 'es'
        ? 'Somos un equipo dedicado a mantener su hogar impecable con servicios de limpieza confiables y profesionales.'
        : 'We are a dedicated team keeping your home spotless with reliable and professional cleaning services.';
    }
    return t(fallbackKey);
  };

  return (
    <section id="about" className="w-full py-16 md:py-20 bg-white">
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <ScrollReveal className="mb-10 md:mb-14">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-brand-mint" />
            <span className="text-sm font-medium tracking-wider uppercase text-brand-mint">
              {t('about_label')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark font-heading leading-tight mb-4">
            {getContent('about_title', 'about_title')}
          </h2>
          <div className="w-10 h-1 bg-brand-mint rounded-full" />
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {aboutFeatures.map((feature, idx) => (
              <ScrollReveal key={feature.id} delay={idx * 100}>
                <div className="group p-5 md:p-6 rounded-xl bg-brand-light hover:bg-brand-dark transition-all duration-300">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-brand-dark group-hover:bg-brand-mint flex items-center justify-center mb-3 transition-colors duration-300">
                    <i className={`${feature.icon} text-lg md:text-xl text-white group-hover:text-white`} />
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-brand-dark group-hover:text-white mb-2 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-xs md:text-sm text-brand-muted group-hover:text-white/80 leading-relaxed transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Image + Stats */}
          <ScrollReveal delay={200} direction="left">
            <div className="flex flex-col justify-center gap-6">
              {/* Professional Image */}
              <div className="relative w-full aspect-[16/10] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20professional%20cleaning%20team%20in%20matching%20teal%20uniforms%20with%20gloves%20and%20equipment%20working%20together%20in%20a%20bright%20modern%20residential%20home%2C%20viewed%20from%20behind%20and%20side%20angles%20so%20no%20clear%20faces%20are%20visible%2C%20warm%20natural%20daylight%2C%20organized%20living%20space%2C%20premium%20cleaning%20service%20photography%20with%20clean%20simple%20minimal%20background%20and%20soft%20teal%20color%20tones%2C%20two%20people%20maximum&width=800&height=500&seq=801&orientation=landscape"
                  alt="Professional cleaning team at work"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/30 via-teal-800/20 to-transparent" />
              </div>

              <p className="text-base md:text-lg text-brand-muted leading-relaxed">
                {getContent('about_description', 'about_description')}
              </p>
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {aboutStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="p-5 md:p-6 rounded-xl bg-brand-light text-center"
                  >
                    <div className="text-2xl md:text-4xl font-bold text-brand-dark font-heading mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-brand-muted">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}